import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Gauge,
  AlertCircle,
  Check,
  Loader,
  MapPin,
  Hash,
  Calendar,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import analyticsService from "../../lib/AnalyticsService";
import { MOCK_US_STATES } from "../PostAuthenticationSurvey";
import VehicleInfoService from "../../lib/VehicleInfoService";

const mapStateToAbbreviation = (stateName) => {
  if (!stateName) return "";
  const matchedState = MOCK_US_STATES.find(
    (state) => state.text.toLowerCase() === stateName.toLowerCase()
  );
  return matchedState ? matchedState.value : stateName;
};

const YEARS = Array.from(
  { length: 25 },
  (_, i) => new Date().getFullYear() - i
);

const RefinancingVehicleCompletion = ({
  selectedVehicle,
  onComplete,
  onBack,
}) => {
  const { user, getIdToken } = useAuth();

  // Determine what we already have — mirrors backend logic:
  // 1. Has VIN → vin + mileage + state. Done.
  // 2. Has license plate → licensePlate + mileage + state. Done.
  // 3. Neither → needs make/model/year as fallback.
  const hasVIN = !!(selectedVehicle?.vin && selectedVehicle.vin.length > 5);
  const hasLicense = !!(selectedVehicle?.plate);
  const hasMakeModelYear = !!(selectedVehicle?.make && selectedVehicle?.model && selectedVehicle?.year);
  const hasState = !!(selectedVehicle?.state);

  const [formData, setFormData] = useState(() => {
    const cachedData = sessionStorage.getItem("refinancingVehicleData");

    // Get state from contact info cache as a fallback
    let stateFromContact = "";
    try {
      const cachedAddressComponents = sessionStorage.getItem(
        "contactInfoAddressComponents"
      );
      if (cachedAddressComponents) {
        const addressData = JSON.parse(cachedAddressComponents);
        stateFromContact = addressData.state || "";
      }
    } catch (e) {
      console.error("Error parsing cached contact data:", e);
    }

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        return {
          vin: parsed.vin || selectedVehicle?.vin || "",
          license: parsed.license || selectedVehicle?.plate || "",
          state:
            parsed.state ||
            stateFromContact ||
            mapStateToAbbreviation(selectedVehicle?.state) ||
            "",
          mileage: parsed.mileage || selectedVehicle?.mileage || "",
          loanBalance: parsed.loanBalance || "",
          monthlyPayment: parsed.monthlyPayment || "",
          year: parsed.year || selectedVehicle?.year || "",
          make: parsed.make || selectedVehicle?.make || "",
          model: parsed.model || selectedVehicle?.model || "",
        };
      } catch (e) {
        console.error("Error parsing cached refinancing data:", e);
      }
    }
    return {
      vin: selectedVehicle?.vin || "",
      license: selectedVehicle?.plate || "",
      state: stateFromContact || mapStateToAbbreviation(selectedVehicle?.state) || "",
      mileage: selectedVehicle?.mileage || "",
      loanBalance: "",
      monthlyPayment: "",
      year: selectedVehicle?.year || "",
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
    };
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState({
    makes: [],
    models: [],
  });
  const [loadingStates, setLoadingStates] = useState({
    makes: false,
    models: false,
  });

  // Only need make/model/year if we have neither VIN nor license plate
  const needsMakeModelYear = !hasVIN && !hasLicense && !hasMakeModelYear;

  useEffect(() => {
    analyticsService.trackEvent("Refinancing Page View", {
      page: "refi_vehicle_completion",
      screen: "vehicle_completion",
      flow_type: "refinancing",
      has_vin: hasVIN,
      has_license: hasLicense,
    });
  }, []);

  useEffect(() => {
    if (Object.values(formData).some((val) => val !== "" && val !== null)) {
      sessionStorage.setItem(
        "refinancingVehicleData",
        JSON.stringify(formData)
      );
    }
  }, [formData]);

  useEffect(() => {
    let mileage = selectedVehicle?.mileage || "";
    const cachedData = sessionStorage.getItem("refinancingVehicleData");
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        mileage = parsed.mileage || selectedVehicle?.mileage || "";
      } catch (e) {}
    }

    // Get state from contact info as fallback
    let stateVal = mapStateToAbbreviation(selectedVehicle?.state) || "";
    if (!stateVal) {
      try {
        const cachedAddr = sessionStorage.getItem("contactInfoAddressComponents");
        if (cachedAddr) {
          stateVal = JSON.parse(cachedAddr).state || "";
        }
      } catch (e) {}
    }

    setFormData({
      vin: selectedVehicle?.vin || "",
      license: selectedVehicle?.plate || "",
      state: stateVal,
      mileage: mileage,
      loanBalance: "",
      monthlyPayment: "",
      year: selectedVehicle?.year || "",
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
    });
  }, [selectedVehicle]);

  // Fetch makes if needed
  useEffect(() => {
    const loadMakes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, makes: true }));
        const token = await getIdToken();
        const makes = await VehicleInfoService.fetchVehicleMakes(token);
        setVehicleOptions((prev) => ({ ...prev, makes }));
      } catch (error) {
        console.error("Error fetching makes:", error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, makes: false }));
      }
    };
    if (user && needsMakeModelYear) loadMakes();
  }, [user, getIdToken, needsMakeModelYear]);

  useEffect(() => {
    if (!needsMakeModelYear) return;
    const loadModels = async () => {
      if (!formData.make) {
        setVehicleOptions((prev) => ({ ...prev, models: [] }));
        return;
      }
      try {
        setLoadingStates((prev) => ({ ...prev, models: true }));
        const token = await getIdToken();
        const models = await VehicleInfoService.fetchVehicleModels(formData.make, token);
        setVehicleOptions((prev) => ({ ...prev, models }));
      } catch (error) {
        console.error("Error fetching models:", error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, models: false }));
      }
    };
    loadModels();
  }, [formData.make, getIdToken, needsMakeModelYear]);

  // All 3 methods require state + mileage. Make/model/year only when no VIN and no plate.
  const getRequiredFields = () => {
    const required = {};
    required.state = "State is required";
    required.mileage = "Mileage is required";
    if (needsMakeModelYear) {
      required.make = "Make is required";
      required.model = "Model is required";
      required.year = "Year is required";
    }
    return required;
  };

  const handleInputChange = (field, value) => {
    if (field === "loanBalance" || field === "monthlyPayment") {
      const numericValue = value.replace(/[^\d.]/g, "");
      setFormData((prev) => ({ ...prev, [field]: numericValue }));
    } else if (field === "vin" || field === "license") {
      setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();

    Object.entries(requiredFields).forEach(([field, errorMessage]) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = errorMessage;
      }
    });

    if (formData.mileage && (isNaN(formData.mileage) || parseInt(formData.mileage) < 0)) {
      newErrors.mileage = "Please enter a valid mileage";
    }

    if (formData.vin && formData.vin.length > 0 && formData.vin.length !== 17) {
      newErrors.vin = "VIN must be exactly 17 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const completeRefinancingData = {
        ...formData,
        mileage: parseInt(formData.mileage),
        completedAt: new Date().toISOString(),
        originalVehicle: selectedVehicle,
      };

      sessionStorage.setItem(
        "completeRefinancingData",
        JSON.stringify(completeRefinancingData)
      );

      analyticsService.trackEvent("Refinancing Vehicle Completion Submit", {
        flow_type: "refinancing",
        has_vin: !!formData.vin,
        has_mileage: !!formData.mileage,
        has_license: !!formData.license,
        has_state: !!formData.state,
      });

      if (onComplete) {
        onComplete(completeRefinancingData);
      }
    } catch (err) {
      console.error("Error completing refinancing information:", err);
      setErrors({
        general: "Failed to process refinancing information. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build the "what we know" summary
  const knownParts = [];
  if (selectedVehicle?.year) knownParts.push(selectedVehicle.year);
  if (selectedVehicle?.make) knownParts.push(selectedVehicle.make);
  if (selectedVehicle?.model) knownParts.push(selectedVehicle.model);
  const knownVehicleName = knownParts.length > 0 ? knownParts.join(" ") : null;

  const knownDetails = [];
  if (selectedVehicle?.vin) knownDetails.push(`VIN: ${selectedVehicle.vin}`);
  if (selectedVehicle?.plate) knownDetails.push(`Plate: ${selectedVehicle.plate}`);

  const headerSubtitle = needsMakeModelYear
    ? "Please provide your vehicle details, state, and current mileage."
    : "Almost done! Confirm your state and enter your current mileage.";

  const inputClass = (field) =>
    `w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B61FF] transition-colors ${
      errors[field] ? "border-red-400" : "border-gray-300"
    }`;

  const inputWithIconClass = (field) =>
    `w-full pl-10 p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7B61FF] transition-colors ${
      errors[field] ? "border-red-400" : "border-gray-300"
    }`;

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-2xl font-bold font-bebas uppercase">
          Confirm Vehicle Details
        </h1>
        <p className="text-gray-600 text-sm">{headerSubtitle}</p>
      </div>

      {/* What we know — green summary card */}
      {(knownVehicleName || knownDetails.length > 0) && (
        <div className="bg-[#D4F6C9] rounded-2xl p-4 space-y-1">
          <p className="text-xs uppercase tracking-wide font-bold text-gray-600 mb-2">
            What We Have
          </p>
          {knownVehicleName && (
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
              <p className="text-sm font-bold text-darkPurple">{knownVehicleName}</p>
            </div>
          )}
          {knownDetails.map((detail, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-700 flex-shrink-0" />
              <p className="text-sm text-gray-600">{detail}</p>
            </div>
          ))}
        </div>
      )}

      {/* Form — only show fields we still need */}
      <div className="border-2 border-[#7B61FF]/30 rounded-2xl p-5 bg-[#F5F2FF]/50 space-y-5">
        <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
          Complete Your Details
        </p>

        {/* State — always required */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            State <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
            <select
              value={formData.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
              className={`${inputWithIconClass("state")} appearance-none`}
            >
              <option value="">Select state...</option>
              {MOCK_US_STATES.map((state) => (
                <option key={state.text} value={state.value}>
                  {state.text}
                </option>
              ))}
            </select>
          </div>
          {errors.state && (
            <p className="text-red-500 text-xs">{errors.state}</p>
          )}
        </div>

        {/* Make/Model/Year — only if no VIN and no license plate */}
        {needsMakeModelYear && (
          <>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Year <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                <select
                  value={formData.year}
                  onChange={(e) => handleInputChange("year", e.target.value)}
                  className={`${inputWithIconClass("year")} appearance-none`}
                >
                  <option value="">Select year...</option>
                  {YEARS.map((year) => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              {errors.year && (
                <p className="text-red-500 text-xs">{errors.year}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Make <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.make}
                onChange={(e) => handleInputChange("make", e.target.value)}
                disabled={loadingStates.makes}
                className={`${inputClass("make")} disabled:bg-gray-100`}
              >
                <option value="">
                  {loadingStates.makes ? "Loading makes..." : "Select make..."}
                </option>
                {vehicleOptions.makes.map((make) => (
                  <option key={make.id} value={make.name}>
                    {make.name}
                  </option>
                ))}
              </select>
              {errors.make && (
                <p className="text-red-500 text-xs">{errors.make}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Model <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.model}
                onChange={(e) => handleInputChange("model", e.target.value)}
                disabled={!formData.make || loadingStates.models}
                className={`${inputClass("model")} disabled:bg-gray-100`}
              >
                <option value="">
                  {loadingStates.models
                    ? "Loading models..."
                    : !formData.make
                    ? "Select make first"
                    : "Select model..."}
                </option>
                {vehicleOptions.models.map((model) => (
                  <option key={model.id} value={model.name}>
                    {model.name}
                  </option>
                ))}
              </select>
              {errors.model && (
                <p className="text-red-500 text-xs">{errors.model}</p>
              )}
            </div>
          </>
        )}

        {/* Mileage — always required */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Current Mileage <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Gauge className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="number"
              value={formData.mileage}
              onChange={(e) => handleInputChange("mileage", e.target.value)}
              className={inputWithIconClass("mileage")}
              placeholder="Enter current mileage"
              min="0"
              max="500000"
            />
          </div>
          {errors.mileage && (
            <p className="text-red-500 text-xs">{errors.mileage}</p>
          )}
        </div>
      </div>

      {/* What Happens Next */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
          What Happens Next
        </p>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">1</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">We find the best rates</span>{" "}
              — based on your vehicle and current loan details.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">2</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Your reward is released</span>{" "}
              — directly to you once your profile is approved.
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {errors.general && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{errors.general}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
        >
          {isSubmitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {onBack && (
          <button
            onClick={onBack}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
};

export default RefinancingVehicleCompletion;
